// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Editor allowing only for updating the length of a given deck edge.
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { cloneTemplate } from "../../../utils/index.js"
import { tweenDeckEditor } from "../../../layouts/deck-editor/tween.js"

// @@imports-package
import { LoadingContainer, MeasurementInput, SteppedContainer } from "../../index.js"

// @@imports-module
import { Editor } from "./Editor.js"

// @@imports-types
import { State } from "../../../types/index.js"
import { EdgeTypeEditor } from "./EdgeTypeEditor.js"
import { App } from "../../../app/App.js"
import { DoubleLinkedListNode, Edge } from "../../../../../package/index.js"

// @@body
class EdgeLengthEditor extends Editor {
    /**
     * 
     * @param {State} state 
     */
    constructor() {
        super()
    }

    connectedCallback() {
        super.connectedCallback()

        const input = cloneTemplate("edge-length-editor-input")
        const nav = cloneTemplate("edge-length-editor-nav")
        this.append(input, nav)

        const modalContainer = SteppedContainer.modalContainer()
        const modal = App.state.deck.type === "shed"
            ? cloneTemplate("edge-length-editor-shed-modal")
            : cloneTemplate("edge-length-editor-modal")
        modal
            .querySelector("button.primary")
            ?.addEventListener("click", () => {
                this.container.unloadModal()
            })
        modal
            .querySelector("button.secondary")
            ?.addEventListener("click", () => {
                this.renderer.renderFeatures.types = true
                this.renderer.render({ reset: true })
                this.container.unloadModal()
                this.remove()
                // this.container.unloadPopover()
            })
        modalContainer.append(modal)
        this.container.loadModal(modalContainer)

        this.#lengthInput.value = this.edge.magnitude
        this.#lengthInput.updateSelectedUnitInputs()

        this.#lengthInput.addEventListener("change", () => {
            const length = this.edge.magnitude
            const deltaLength = this.#lengthInput.value - length
            if (deltaLength === 0) { return }

            this.tweening = true
            this.renderer.tween(progress => {
                const resizedLength = length + deltaLength * progress
                this.deck.resizeEdge(this.edge, Math.round(resizedLength))

                if (progress === 1) {
                    this.tweening = false
                    const event = new Event("tween-end")
                    setTimeout(() => this.dispatchEvent(event), 100)
                }
            })
        })

        const nextEdgeButton = /** @type {HTMLButtonElement} */ (this
            .querySelector("button.next-edge"))
        nextEdgeButton.addEventListener("click", () => {
            const switchEdge = () => {
                const { dimensions, types } = this.deck.isDefined
                if (dimensions) {
                    if (types) {
                        this.renderer.selectedEdge = null
                        this.remove()

                        document.querySelector("stepped-container > nav button.primary")
                            ?.classList
                            .remove("inactive")

                        const modalContainer = SteppedContainer.modalContainer()
                        const modal = cloneTemplate("advanced-mode-selector-modal")
                        modal.querySelector("button.primary")
                            ?.addEventListener("click", () => {
                                this.container.unloadModal().then(() => {
                                    this.container.incrementStep()
                                })
                            })
                        modal.querySelector("button.secondary")
                            ?.addEventListener("click", () => {
                                this.container.unloadModal()
                            })
                        modalContainer.append(modal)
                        this.container.loadModal(modalContainer)
                    }
                    else {
                        this.renderer.selectedEdge = null
                        const edgeTypeEditor = new EdgeTypeEditor()
                        this.replaceWith(edgeTypeEditor)
                    }

                    return
                }

                const edgeNode = this.deck.edges.nodeWithValue(this.edge)

                const resizeListener = () => {
                    this.removeEventListener("tween-end", resizeListener)
                    this.edge = /** @type {any} */ (edgeNode?.next?.value)
                }

                if (this.tweening) {
                    this.addEventListener("tween-end", resizeListener)
                    this.#lengthInput.blur()
                }
                else { this.edge = /** @type {any} */ (edgeNode?.next?.value) }
            }

            if (!this.deck.isSetEdgeLength(this.edge)) {
                const modalContainer = SteppedContainer.modalContainer()
                const modal = cloneTemplate("edge-length-confirmation-modal")
                modal.querySelector("button.primary")?.addEventListener("click", () => {
                    this.container.unloadModal()
                    this.deck.resizeEdge(this.edge, this.edge.magnitude)
                    switchEdge()
                })
                modal.querySelector("button.secondary")?.addEventListener("click", () => {
                    this.container.unloadModal()
                })
                modalContainer.append(modal)
                this.container.loadModal(modalContainer)
            }
            else {
                // switchEdge()
                const resizeListener = () => {
                    this.removeEventListener("tween-end", resizeListener)
                    switchEdge()
                }

                if (this.tweening) {
                    this.addEventListener("tween-end", resizeListener)
                    this.#lengthInput.blur()
                }
                else { switchEdge() }
            }
        })

        // handle previous button such that if it is clicked on first edge, that
        // previous step is loaded instead
        // const previousEdgeButton = this.querySelector("button.previous-edge")
        // previousEdgeButton.addEventListener("click", () => {
        //     const edgeNode = /** @type {DoubleLinkedListNode<Edge>} */ (this
        //         .state
        //         .deckEditor
        //         .deck
        //         ?.edges
        //         .nodeWithValue(this.edge))

        //     const resizeListener = () => {
        //         this.removeEventListener("tween-end", resizeListener)
        //         this.edge = edgeNode.previous?.value
        //     }

        //     if (this.tweening) {
        //         this.addEventListener("tween-end", resizeListener)
        //         this.#lengthInput.blur()
        //     }
        //     else { this.edge = edgeNode.previous?.value }
        // });
    }

    set edge(edge) {
        const setEdge = () => {
            this.renderer.selectedEdge = edge
            this.#lengthInput.value = edge.magnitude
            this.#lengthInput.updateSelectedUnitInputs()
        }

        const resizeListener = () => {
            this.removeEventListener("tween-end", resizeListener)
            setEdge()
        }

        if (this.tweening) {
            this.addEventListener("tween-end", resizeListener)
            this.#lengthInput.blur()
        }
        else { setEdge() }
    }

    get edge() {
        return /** @type {Edge} */ (this.renderer.selectedEdge)
    }

    get #lengthInput() {
        return /** @type {MeasurementInput} */ (this
            .querySelector("measurement-input"))
    }

    static get customName() {
        return "edge-length-editor"
    }
}

// @@exports
export { EdgeLengthEditor }
